<script>
  import Multiselect from 'vue-multiselect'
  import { MapPinIcon, PlusIcon, SearchIcon } from 'vue-feather-icons'
  import { parseUnitStreetAddressData } from '@utils/format-address';

  export default {
    components: {
      Multiselect, MapPinIcon, PlusIcon, SearchIcon
    },
    inheritAttrs: false,
    props: {
      id: {
        type: String,
        default: '',
      },
      value: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: 'E.g. 123 Paper St Fitzroy 3101',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      showAddManual: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        options: [],
        limitCountries: [],
        geocoder: new window.google.maps.Geocoder(),
        autocompleteService: new window.google.maps.places.AutocompleteService(),
      }
    },
    computed: {
      inputVal: {
        get: function () {
          return this.value || null;
        },
        set: function (newValue) {
          this.$emit('input', newValue);
        }
      }
    },
    created() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async position => {
          const latlng = { lat: position.coords.latitude, lng: position.coords.longitude };
          this.geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === "OK") {
              const countryCode = this.getCountryCodeByGeocode(results);
              if (countryCode) {
                this.limitCountries = [countryCode];
              }
            }
          });
        });
      }
    },
    methods: {
      parseUnitStreetAddressData,
      extractFromAddress(components, type) {
        return components
          .filter(component => component.types.indexOf(type) === 0)
          .map(item => item.long_name || item.short_name).pop() || null;
      },
      parseUnitStreetData(components, type, streetNumber) {
        const geoDataValue = this.extractFromAddress(components, type)
        return geoDataValue !== null
          ? geoDataValue
          : this.parseUnitStreetAddressData(streetNumber)[type]
      },
      onSelectAddress(selectedOption) {
        this.geocoder.geocode(
          { placeId: selectedOption.place_id },
          (data, status) => {
            if (status !== 'OK') return;
            if (!data[0]) return;
            const {
              address_components: addressComponents,
              formatted_address: formattedAddress
            } = data[0];
            this.inputVal = formattedAddress;
            const result = {
              unit: this.parseUnitStreetData(addressComponents, 'subpremise', selectedOption.description),
              unit_seperator: this.parseUnitStreetAddressData(selectedOption.description).unit_seperator,
              street_number: this.parseUnitStreetData(addressComponents, 'street_number', selectedOption.description),
              route: this.parseUnitStreetData(addressComponents, 'route', selectedOption.description),
              postal_code: this.extractFromAddress(addressComponents, 'postal_code'),
              locality: this.extractFromAddress(addressComponents, 'locality'),
              administrative_area_level_1: this.extractFromAddress(addressComponents, 'administrative_area_level_1'),
              country: this.extractFromAddress(addressComponents, 'country'),
            }
            this.$emit('placechanged', result, data[0], this.id)
          }
        )
      },
      onSelectAddManual() {
        this.$emit('addManualSelected')
        this.$refs.address.toggle();
      },
      asyncFind (query) {
        if (!query) return;
        this.autocompleteService.getPlacePredictions(
          { input: query, componentRestrictions: { country: this.limitCountries } },
          (data, status) => {
            if (status !== 'OK') return;
            this.options = data;
          }
        )
      },
      getCountryCodeByGeocode (geocodeResults) {
        if (!geocodeResults) {
          return null;
        }

        const countryAddress = geocodeResults.find(
          address => address.types && address.types.includes('country')
        );
        if (!countryAddress || !countryAddress.address_components) {
          return null;
        }

        const addressComponent = countryAddress.address_components.find(
          address => address.types && address.types.includes('country')
        );
        if (!addressComponent || !addressComponent.short_name) {
          return null;
        }

        return addressComponent.short_name.toLowerCase()
      },
    }
  }
</script>

<template>
  <Multiselect
    :id="id"
    ref="address"
    :value="inputVal"
    :placeholder="inputVal || placeholder"
    :class="{
      'scrollable': options.length > 7,
    }"
    :options="options"
    :options-limit="3"
    :searchable="true"
    :internal-search="false"
    :show-labels="false"
    :hide-selected="true"
    :show-no-results="false"
    :disabled="disabled"
    class="auto-complete-address"
    @select="onSelectAddress"
    @search-change="asyncFind"
  >
    <template slot="singleLabel" slot-scope="{ option }">
      <span>{{ option }}</span>
    </template>
    <template slot="caret">
      <div class="multiselect__select">
        <SearchIcon size="1x" class="custom-class"></SearchIcon>
      </div>
    </template>
    <template slot="option" slot-scope="{ option }">
      <div class="tw-flex align-items-center">
        <MapPinIcon size="1.5x" class="ml-1 mr-2 text-bold"></MapPinIcon>
        <span>{{ option.description }}</span>
      </div>
    </template>
    <template v-if="showAddManual" slot="afterList">
      <li class="multiselect__element" @click="onSelectAddManual">
        <span class="multiselect__option multiselect__option_add_manual">
          <div class="tw-flex align-items-center">
            <button class="btn btn-circle btn-add-new rounded-circle mr-2" @click="$emit('create-action')">
              <PlusIcon size="1.5x" class="fl-inline"/>
            </button>
            <span class="tw-text-positive">Add address manually</span>
          </div>
        </span>
      </li>
    </template>
    <span slot="noOptions">Type to search address</span>
    <span slot="noResult">Oops! No elements found. Consider changing the search query.</span>
  </Multiselect>
</template>
